/**
 * Major commercial airport IATA codes (Europe, transatlantic US/Canada, common hubs).
 * Used to avoid treating name fragments (e.g. "AKI" from a passenger name) as airports when
 * paired with real codes like HEL — we only accept legs where both endpoints are in this set.
 */

const MAJOR_IATA_LIST: string[] = [
  // Finland & Nordics
  'HEL',
  'OUL',
  'TMP',
  'TKU',
  'ARN',
  'GOT',
  'MMX',
  'OSL',
  'BGO',
  'SVG',
  'TRD',
  'CPH',
  'BLL',
  'AAL',
  'KEF',
  // UK & Ireland
  'LHR',
  'LGW',
  'STN',
  'LTN',
  'LCY',
  'MAN',
  'EDI',
  'GLA',
  'BHX',
  'BRS',
  'NCL',
  'DUB',
  'ORK',
  'SNN',
  'BFS',
  // France
  'CDG',
  'ORY',
  'NCE',
  'LYS',
  'TLS',
  'BOD',
  'MRS',
  // Benelux
  'AMS',
  'RTM',
  'EIN',
  'BRU',
  'CRL',
  'LUX',
  // Germany
  'FRA',
  'MUC',
  'DUS',
  'TXL',
  'BER',
  'STR',
  'HAM',
  'CGN',
  'LEJ',
  'NUE',
  'DRS',
  'HAJ',
  // Spain & Portugal
  'MAD',
  'BCN',
  'AGP',
  'PMI',
  'VLC',
  'BIO',
  'LIS',
  'OPO',
  'FNC',
  // Italy
  'FCO',
  'MXP',
  'LIN',
  'VCE',
  'NAP',
  'BLQ',
  'CTA',
  // Switzerland
  'ZUR',
  'GVA',
  'BSL',
  // Central & Eastern Europe
  'VIE',
  'PRG',
  'BUD',
  'WAW',
  'KRK',
  'GDN',
  'OTP',
  'SOF',
  'ZAG',
  'DBV',
  'BEG',
  'TLL',
  'RIX',
  'VNO',
  // Greece & Cyprus
  'ATH',
  'SKG',
  'LCA',
  'PFO',
  // Norway (extra)
  'TRF',
  'AES',
  // Poland
  'WRO',
  'KTW',
  // Balkans
  'LJU',
  'SPU',
  'SKP',
  // Middle East hubs (common EU–US connections)
  'DXB',
  'AUH',
  'DOH',
  'IST',
  'SAW',
  // USA — major / transatlantic
  'JFK',
  'LGA',
  'EWR',
  'SWF',
  'BOS',
  'PVD',
  'MHT',
  'IAD',
  'DCA',
  'BWI',
  'PHL',
  'PIT',
  'CLE',
  'DTW',
  'ORD',
  'MDW',
  'MSP',
  'ATL',
  'CLT',
  'RDU',
  'MIA',
  'FLL',
  'TPA',
  'MCO',
  'JAX',
  'IAH',
  'HOU',
  'DFW',
  'AUS',
  'SAT',
  'DEN',
  'LAS',
  'LAX',
  'SNA',
  'BUR',
  'ONT',
  'SFO',
  'OAK',
  'SJC',
  'SAN',
  'SEA',
  'PDX',
  'PHX',
  'SLC',
  'IND',
  'STL',
  'MCI',
  'BNA',
  'MEM',
  'MSY',
  'OKC',
  'PBI',
  'RSW',
  'BUF',
  'ROC',
  'SYR',
  'ALB',
  'PWM',
  'BGR',
  'CHS',
  'SAV',
  'MYR',
  'ILM',
  'EUG',
  'ANC',
  'HNL',
  // Canada
  'YYZ',
  'YVR',
  'YUL',
  'YYC',
  'YOW',
  'YHZ',
  'YWG',
  // Mexico (common)
  'CUN',
  'MEX',
];

const MAJOR_IATA = new Set(MAJOR_IATA_LIST);

/** True if this 3-letter code is treated as a major commercial airport for route extraction. */
export function isMajorCommercialIata(code: string): boolean {
  const c = code.trim().toUpperCase();
  return c.length === 3 && MAJOR_IATA.has(c);
}

/**
 * First two distinct major IATA codes in left-to-right order (typical boarding-pass layout).
 * Ignores non-whitelisted triplets such as name fragments that look like codes (e.g. AKI).
 */
export function findFirstMajorIataPair(text: string): { origin: string; destination: string } | null {
  const head = text.slice(0, 8000).toUpperCase();
  const re = /\b([A-Z]{3})\b/g;
  const distinct: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(head)) !== null) {
    const code = m[1];
    if (!isMajorCommercialIata(code)) continue;
    if (!distinct.includes(code)) distinct.push(code);
    if (distinct.length >= 2) {
      return { origin: distinct[0], destination: distinct[1] };
    }
  }
  return null;
}
